import { z } from 'zod';

export const LINK_KINDS = ['instagram', 'facebook', 'linkedin', 'twitter', 'website'] as const;
export type LinkKind = (typeof LINK_KINDS)[number];

export const LINK_LABEL: Record<LinkKind, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  twitter: 'X (Twitter)',
  website: 'Website',
};

/** Domains each social field accepts (the site itself or any subdomain). Websites may be anything. */
const HOSTS: Record<Exclude<LinkKind, 'website'>, string[]> = {
  instagram: ['instagram.com'],
  facebook: ['facebook.com', 'fb.com', 'fb.me'],
  linkedin: ['linkedin.com', 'lnkd.in'],
  twitter: ['twitter.com', 'x.com'],
};
const HANDLE_BASE: Partial<Record<LinkKind, string>> = { instagram: 'https://instagram.com/', twitter: 'https://x.com/' };
const MAX = 300;

export type ParsedLink = { value: string | null; error?: undefined } | { value?: undefined; error: string };

/**
 * Turn what someone typed into a safe https URL: "@name" works for Instagram and X, a missing scheme is
 * added, and only http(s) links on the right site are kept. Empty input clears the field.
 */
export function parseLink(kind: LinkKind, raw: string | null | undefined): ParsedLink {
  const text = (raw ?? '').trim();
  if (!text) return { value: null };
  if (text.length > MAX) return { error: 'That link is too long' };
  const bad = { error: kind === 'website' ? 'Enter a valid website address' : `Enter a valid ${LINK_LABEL[kind]} link` };

  const base = HANDLE_BASE[kind];
  let candidate = text;
  if (base && /^@?[A-Za-z0-9._]{1,30}$/.test(text)) candidate = base + text.replace(/^@/, '');
  else if (/^https?:\/\//i.test(text)) candidate = text;
  else if (/^[a-z][a-z0-9+.-]*:(?!\d)/i.test(text)) return bad; // javascript:, data:, mailto: …
  else candidate = `https://${text.replace(/^\/+/, '')}`;

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return bad;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return bad;
  if (url.username || url.password) return bad;
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  if (!host.includes('.') || host.endsWith('.')) return bad;
  if (kind !== 'website' && !HOSTS[kind].some((d) => host === d || host.endsWith(`.${d}`))) return bad;
  const href = url.href;
  return href.length > MAX ? { error: 'That link is too long' } : { value: href };
}

const linkField = (kind: LinkKind) =>
  z
    .string()
    .nullish()
    .transform((v, ctx) => {
      const r = parseLink(kind, v);
      if (r.error !== undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: r.error });
        return z.NEVER;
      }
      return r.value;
    });

/** Zod fields for the five profile links; values come out normalised (or null). */
export const linkFields = {
  instagram: linkField('instagram'),
  facebook: linkField('facebook'),
  linkedin: linkField('linkedin'),
  twitter: linkField('twitter'),
  website: linkField('website'),
};

/** Short text to show on a link: the handle for social sites, the host for a website. */
export function linkText(kind: LinkKind, href: string): string {
  try {
    const u = new URL(href);
    if (kind === 'website') return u.hostname.replace(/^www\./, '') + (u.pathname.length > 1 ? u.pathname.replace(/\/$/, '') : '');
    const seg = u.pathname.split('/').filter(Boolean);
    const last = kind === 'linkedin' ? seg[1] ?? seg[0] : seg[0];
    return last ? (kind === 'instagram' || kind === 'twitter' ? `@${last}` : last) : LINK_LABEL[kind];
  } catch {
    return LINK_LABEL[kind];
  }
}

const EMAIL_RE = /^[^\s@<>(),;:"[\]\\]+@[^\s@<>(),;:"[\]\\]+\.[^\s@<>(),;:"[\]\\]{2,}$/;

/** Tidy an email address: trims, lower-cases, and returns an error for anything that isn't a plain address. */
export function parseEmail(raw: string | null | undefined): ParsedLink {
  const text = (raw ?? '').trim().replace(/^mailto:/i, '');
  if (!text) return { value: null };
  if (text.length > 254 || !EMAIL_RE.test(text) || text.includes('..')) return { error: 'Enter a valid email address' };
  return { value: text.toLowerCase() };
}

export const emailField = z
  .string()
  .nullish()
  .transform((v, ctx) => {
    const r = parseEmail(v);
    if (r.error !== undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: r.error });
      return z.NEVER;
    }
    return r.value;
  });

/** Country calling codes (ITU E.164) that are assigned. 1 and 7 are shared by several countries. */
const CALLING_CODES = new Set<string>([
  '1', '7',
  ...range(20, 20), ...range(27, 27), ...range(30, 34), ...range(36, 36), ...range(39, 41), ...range(43, 49),
  ...range(51, 58), ...range(60, 66), ...range(81, 82), ...range(84, 84), ...range(86, 86), ...range(90, 95), ...range(98, 98),
  '212', '213', '216', '218',
  ...range(220, 258), ...range(260, 269), '290', '291', '297', '298', '299',
  ...range(350, 359), ...range(370, 389), '420', '421', '423',
  ...range(500, 509), ...range(590, 599), ...range(670, 692),
  '800', '808', '850', '852', '853', '855', '856', '870', '871', '872', '873', '874', '878', '881', '882', '883', '886', '888',
  ...range(960, 968), ...range(970, 977), '979', ...range(992, 996), '998',
]);
// a few gaps inside the ranges above that are not assigned
for (const c of ['259', '389']) CALLING_CODES.delete(c);

function range(a: number, b: number): string[] {
  return Array.from({ length: b - a + 1 }, (_, i) => String(a + i));
}

/** Length of the country calling code at the start of `digits` (1–3), or 0 if none matches. */
function callingCodeLength(digits: string): number {
  for (const n of [1, 2, 3]) if (CALLING_CODES.has(digits.slice(0, n))) return n;
  return 0;
}

/**
 * Phone numbers must start with a country code: "+1 415 555 2671", "+44 20 7946 0958", or "00 44 …".
 * Spaces, dashes, dots and brackets are ignored. Stored as "+14155552671" (E.164); empty input clears it.
 */
export function parsePhone(raw: string | null | undefined): ParsedLink {
  const text = (raw ?? '').trim();
  if (!text) return { value: null };
  if (!/^[+\d][\d\s().-]*$/.test(text)) return { error: 'Use digits only, starting with the country code, like +1 415 555 2671' };
  let digits = text.replace(/\D/g, '');
  if (text.startsWith('+')) {
    // keep as is
  } else if (digits.startsWith('00')) {
    digits = digits.slice(2);
  } else {
    return { error: 'Start with the country code, like +1 for the US or +44 for the UK' };
  }
  const cc = callingCodeLength(digits);
  if (!cc) return { error: 'That country code isn’t recognised. Start with + and the country code, like +91' };
  if (digits.length - cc < 4 || digits.length > 15) return { error: 'That number looks too short or too long' };
  return { value: `+${digits}` };
}

/** "+14155552671" → "+1 415 555 2671" for +1; other countries get the code split off: "+44 2079460958". */
export function formatPhone(e164: string): string {
  const digits = e164.replace(/\D/g, '');
  const cc = callingCodeLength(digits);
  if (!cc) return e164;
  const rest = digits.slice(cc);
  if (digits.startsWith('1') && rest.length === 10) return `+1 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
  return `+${digits.slice(0, cc)} ${rest}`;
}

export const phoneField = z
  .string()
  .nullish()
  .transform((v, ctx) => {
    const r = parsePhone(v);
    if (r.error !== undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: r.error });
      return z.NEVER;
    }
    return r.value;
  });
